// src/pinia/employeeInsightsStore.js
import { defineStore } from 'pinia'
import { useAuthStore } from './authStore'
import { useDsaReportsStore } from './dsaReportsStore'
import {
  getEmployeeInsightsActivities,
  getEmployeeInsightsActivitySummary,
  getEmployeeInsightsStaff,
  getEmployeeInsightsStaffById,
  getEmployeeInsightsTasks,
  getEmployeeInsightsWeeklyTargets
} from '../features/employee-insights/api/employeeInsightsApi'

export const INTERNAL_NON_BILLABLE_CLASSES = new Set([3, 5, 11, 12, 13, 14, 15])
const INTERNAL_MILESTONE_TITLES = new Set([
  'internal meetings',
  'benefits',
  'client engagement',
  'operations',
  'training',
  'other',
  'internal technical activities'
])
const INTERNAL_JOB_TITLE_KEYWORDS = ['perbyte internal activities']

function resolveActivityClass(activity) {
  return activity?.activity_class ?? activity?.ActivityClass ?? activity?.activityClass ?? activity?.class ?? activity?.Class
}

function isInternalClass(activityClass) {
  const numeric = Number(activityClass)
  return Number.isFinite(numeric) && INTERNAL_NON_BILLABLE_CLASSES.has(numeric)
}

function breadcrumbsInternalReason(activity) {
  const breadcrumbs = activity?.breadcrumbs ?? activity?.Breadcrumbs
  if (!Array.isArray(breadcrumbs)) return null

  for (const crumb of breadcrumbs) {
    const table = (crumb?.table ?? '').toString().toLowerCase()
    const title = (crumb?.title ?? '').toString().toLowerCase()
    if (!table || !title) continue

    if (table === 'milestone' && INTERNAL_MILESTONE_TITLES.has(title)) {
      return `breadcrumb:milestone:${crumb.title ?? ''}`
    }

    if (table === 'job' && INTERNAL_JOB_TITLE_KEYWORDS.some(keyword => title.includes(keyword))) {
      return `breadcrumb:job:${crumb.title ?? ''}`
    }
  }

  return null
}

function classifyActivityInternal(activity) {
  const cls = resolveActivityClass(activity)
  if (isInternalClass(cls)) {
    return { isInternal: true, reason: `class:${cls}` }
  }

  const breadcrumbReason = breadcrumbsInternalReason(activity)
  if (breadcrumbReason) {
    return { isInternal: true, reason: breadcrumbReason }
  }

  return { isInternal: false, reason: null }
}

const DEFAULT_ORG_TIMEZONE = process.env.VUE_APP_ORG_TIMEZONE || 'America/Chicago'
const pendingActivitySummaryRequests = new Map()
const pendingDailyStatsByStaffRequests = new Map()
const activitySummaryAbortControllers = new Map()
const MAX_CONCURRENT_WEEK_SUMMARY_REQUESTS = 3

export function getActivityNonBillableType(activity) {
  return classifyActivityInternal(activity).isInternal ? 'Internal' : 'Client'
}

function summaryToDailyStat(summary) {
  const billSec = Number(summary.billableSeconds) || 0
  const totalSec = Number(summary.totalSeconds) || 0
  const nonBillClientSec = Number(summary.clientNonBillableSeconds) || 0
  const nonBillInternalSec = Number(summary.internalNonBillableSeconds) || 0
  const ptoSec = Number(summary.ptoSeconds) || 0
  const clientHrs = nonBillClientSec / 3600
  const internalHrs = nonBillInternalSec / 3600
  const ptoHrs = ptoSec / 3600
  const totalHrs = totalSec / 3600

  return {
    date: summary.date,
    billableHrs: billSec / 3600,
    totalHrs,
    clientNonBillableHrs: clientHrs,
    internalNonBillableHrs: internalHrs,
    ptoHrs,
    billablePct: totalSec ? (billSec / totalSec) * 100 : 0,
    clientNonBillablePct: totalSec ? (nonBillClientSec / totalSec) * 100 : 0,
    internalNonBillablePct: totalSec ? (nonBillInternalSec / totalSec) * 100 : 0,
    ptoPct: totalSec ? (ptoSec / totalSec) * 100 : 0
  }
}

export const useEmployeeInsightsStore = defineStore('employeeInsightsStore', {
  state: () => {
    let storedDateRange = { startDate: null, endDate: null }
    if (typeof window !== 'undefined') {
      try {
        storedDateRange = JSON.parse(window.localStorage.getItem('employeeInsightsDateRange') || '{}') || {}
      } catch (err) {
        storedDateRange = { startDate: null, endDate: null }
        console.warn('Failed to parse employee insights date range from storage:', err)
      }
    }

    return {
    // Staff
    staffList: [],
    selectedStaff: null,

    // Activities
    activitiesList: [],

    // Tasks
    tasksList: [],

    // Derived per-day stats (aggregated all staff)
    dailyStats: [],
    dailyStatsRangeKey: null,

    // Derived per-day stats, per staff
    dailyStatsByStaff: {},
    dailyStatsByStaffDateIndex: {},
    dailyStatsByStaffRangeKey: null,
    dailyStatsByStaffLoadingWeeks: {},
    dailyStatsByStaffLoadedWeeks: {},
    dailyStatsByStaffErrorWeeks: {},
    dailyStatsByStaffLoadProgress: {
      totalWeeks: 0,
      completedWeeks: 0,
      activeWeeks: 0,
      failedWeeks: 0,
      currentWeekLabel: ''
    },
    activitySummaryCancelRequested: false,

    // Shared loading/error
    isLoading: false,
    isActivitySummaryLoading: false,
    error: null,

    // Date window (epoch seconds)
    startDate: storedDateRange.startDate ?? null,
    endDate: storedDateRange.endDate ?? null,

    // Weekly hour targets
    defaultWeeklyHourTarget: 40,
    weeklyHourTargets: {},
  }
},

  actions: {
    // 1) fetch all staff
    async fetchAllStaff({
      limit = 100,
      fields = 'id,firstname,surname,email,access_level,standing',
      filters = 'standing(active)'
    } = {}) {
      const authStore = useAuthStore()
      if (!authStore.token) {
        this.error = 'Authorization token missing'
        return
      }
      this.isLoading = true
      this.error = null

      try {
        const response = await getEmployeeInsightsStaff({ limit, fields, filters })
        this.staffList = response.data
      } catch (err) {
        this.error = err.message || 'Failed to load staff list'
        this.staffList = []
      } finally {
        this.isLoading = false
      }
    },

    // 2) fetch one staff by ID or 'me'
    async fetchStaffById(id, opts = {}) {
      if (id === 'me') {
        return this.fetchCurrentStaff(opts)
      }
      const authStore = useAuthStore()
      if (!authStore.token) {
        this.error = 'Authorization token missing'
        return
      }
      this.isLoading = true
      this.error = null
      this.selectedStaff = null

      try {
        const response = await getEmployeeInsightsStaffById(id, opts)
        this.selectedStaff = response.data
      } catch (err) {
        this.error = err.message || `Failed to load staff #${id}`
        this.selectedStaff = null
      } finally {
        this.isLoading = false
      }
    },

    // 3) fetch the current user by email
    async fetchCurrentStaff({
      fields = 'id,firstname,surname,email,access_level,standing'
    } = {}) {
      const myStore = useDsaReportsStore()
      const authStore = useAuthStore()
      if (!authStore.token) {
        this.error = 'Authorization token missing'
        return
      }
      if (!myStore.email) {
        await myStore.fetchUserData()
      }
      const currentEmail = (myStore.email || '').trim()
      if (!currentEmail) {
        this.error = 'Current user email is unavailable'
        this.selectedStaff = null
        return
      }
      this.isLoading = true
      this.error = null
      this.selectedStaff = null

      try {
        const response = await getEmployeeInsightsStaff({
          fields,
          filters: `email(${currentEmail})`
        })
        this.selectedStaff = response.data[0] ?? this.staffList.find(staff =>
          String(staff.email || '').toLowerCase() === currentEmail.toLowerCase()
        ) ?? null
      } catch (err) {
        this.error = err.message || 'Failed to load current staff'
        this.selectedStaff = null
      } finally {
        this.isLoading = false
      }
    },

    // 4) fetch activities for a given window & optional owner
    async fetchActivitiesData({
      limit = 100,
      offset = 0,
      fields = '_ALL',
      startDate,
      endDate,
      ownerId
    } = {}) {
      const authStore = useAuthStore()
      if (!authStore.token) {
        this.error = 'Authorization token missing'
        return []
      }
      this.isLoading = true
      this.error = null

      try {
        const resp = await getEmployeeInsightsActivities({ limit, offset, fields, startDate, endDate, ownerId })
        return Array.isArray(resp.data) ? resp.data : []
      } catch (err) {
        this.error = err.message || 'Failed to load activities'
        return []
      } finally {
        this.isLoading = false
      }
    },

    async fetchActivitiesList({
      limit = 100,
      offset = 0,
      fields = 'id,subject,date_logged,billable,nonbillable,owner_id,against_id,against_type,activity_class,breadcrumbs(_ALL)',
      startDate,
      endDate,
      ownerId
    } = {}) {
      this.activitiesList = []
      this.activitiesList = await this.fetchActivitiesData({
        limit,
        offset,
        fields,
        startDate,
        endDate,
        ownerId
      })
    },

    async fetchActivities(opts = {}) {
      return this.fetchActivitiesList(opts)
    },

    async fetchActivitySummaryData({
      limit = 100,
      startDate,
      endDate,
      ownerId,
      timeZone = DEFAULT_ORG_TIMEZONE,
      useGlobalLoading = true,
      signal = null
    } = {}) {
      const authStore = useAuthStore()
      if (!authStore.token) {
        this.error = 'Authorization token missing'
        return []
      }

      const requestKey = JSON.stringify({
        endpoint: 'activity-summary',
        limit,
        startDate,
        endDate,
        ownerId,
        timeZone
      })
      if (pendingActivitySummaryRequests.has(requestKey)) {
        console.info('[Staff Insights] reusing in-flight activity summary request', {
          durationMs: 0,
          startDate,
          endDate,
          ownerId,
          limit,
          timeZone
        })
        return pendingActivitySummaryRequests.get(requestKey)
      }

      if (useGlobalLoading) {
        this.isLoading = true
        this.isActivitySummaryLoading = true
      }
      this.error = null

      const requestPromise = (async () => {
        const requestAbortController = typeof AbortController !== 'undefined'
          ? new AbortController()
          : null
        if (requestAbortController) {
          activitySummaryAbortControllers.set(requestKey, requestAbortController)
        }
        if (signal && requestAbortController) {
          signal.addEventListener('abort', () => requestAbortController.abort(), { once: true })
        }
        const requestStartedAt = performance.now()
        console.info('[Staff Insights] activity summary request started', {
          durationMs: 0,
          startDate,
          endDate,
          ownerId,
          limit,
          timeZone
        })
        const resp = await getEmployeeInsightsActivitySummary(
          { limit, startDate, endDate, ownerId, timeZone },
          { signal: requestAbortController?.signal || signal }
        )
        const responseReceivedAt = performance.now()
        const rows = Array.isArray(resp.data) ? resp.data : []
        const rowsNormalizedAt = performance.now()
        console.info('[Staff Insights] activity summary response', {
          durationMs: Math.round(responseReceivedAt - requestStartedAt),
          startDate,
          endDate,
          ownerId,
          status: resp.status,
          rowCount: rows.length,
          requestAndJsonParseMs: Math.round(responseReceivedAt - requestStartedAt),
          rowsNormalizeMs: Math.round(rowsNormalizedAt - responseReceivedAt),
          firstRow: rows[0] || null,
          lastRow: rows[rows.length - 1] || null
        })
        return rows
      })()

      pendingActivitySummaryRequests.set(requestKey, requestPromise)

      try {
        return await requestPromise
      } catch (err) {
        const wasCanceled = err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || err?.name === 'AbortError'
        if (wasCanceled) {
          console.info('[Staff Insights] activity summary request canceled', {
            durationMs: null,
            startDate,
            endDate,
            ownerId
          })
          return []
        }
        const message = err.response?.data || err.message || 'Failed to load activity summary'
        this.error = message
        console.error('[Staff Insights] activity summary request failed', {
          durationMs: null,
          startDate,
          endDate,
          ownerId,
          message,
          status: err.response?.status,
          data: err.response?.data
        })
        return []
      } finally {
        pendingActivitySummaryRequests.delete(requestKey)
        activitySummaryAbortControllers.delete(requestKey)
        if (useGlobalLoading) {
          this.isLoading = false
          this.isActivitySummaryLoading = false
        }
      }
    },

    // 5) fetch tasks using flexible filters
    async fetchTasks({
      limit = 100,
      fields = 'id,title,description,status,standing,assignee,date_created,date_due,date_completed,billable,nonbillable,against_type,against_id,breadcrumbs(_ALL),remaining,object_budget,logged',
      filters
    } = {}) {
      const authStore = useAuthStore()
      if (!authStore.token) {
        this.error = 'Authorization token missing'
        return
      }
      this.isLoading = true
      this.error = null
      this.tasksList = []

      try {
        const response = await getEmployeeInsightsTasks({ limit, fields, filters })
        this.tasksList = response.data
      } catch (err) {
        this.error = err.message || 'Failed to load tasks'
      } finally {
        this.isLoading = false
      }
    },

    // 6) derive dailyStats (all staff)
    async fetchDailyStats({
      limit = 100,
      startDate,
      endDate,
      ownerId
    } = {}) {
      const rangeKey = `${startDate || ''}-${endDate || ''}-${ownerId || ''}-${limit}`
      if (this.dailyStatsRangeKey === rangeKey && this.dailyStats.length) {
        return
      }
      const summaries = await this.fetchActivitySummaryData({ limit, startDate, endDate, ownerId })

      this.dailyStats = summaries
        .map(summaryToDailyStat)
        .sort((a, b) => (a.date < b.date ? -1 : 1))
      this.dailyStatsRangeKey = rangeKey
    },

    buildWeeklySummaryRanges(startDate, endDate) {
      if (!startDate || !endDate) return []

      const ranges = []
      const end = new Date(endDate * 1000)
      let cursor = new Date(startDate * 1000)
      cursor = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth(), cursor.getUTCDate()))
      const endUtc = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate(), 23, 59, 59))

      while (cursor <= endUtc) {
        const weekStart = new Date(cursor)
        const weekEnd = new Date(cursor)
        weekEnd.setUTCDate(weekEnd.getUTCDate() + (6 - ((weekEnd.getUTCDay() + 6) % 7)))
        weekEnd.setUTCHours(23, 59, 59, 0)

        if (weekEnd > endUtc) {
          weekEnd.setTime(endUtc.getTime())
        }

        const startEpoch = Math.floor(weekStart.getTime() / 1000)
        const endEpoch = Math.floor(weekEnd.getTime() / 1000)
        ranges.push({
          key: `${startEpoch}-${endEpoch}`,
          startDate: startEpoch,
          endDate: endEpoch,
          startIso: weekStart.toISOString().slice(0, 10),
          endIso: weekEnd.toISOString().slice(0, 10)
        })

        cursor = new Date(weekEnd)
        cursor.setUTCDate(cursor.getUTCDate() + 1)
        cursor.setUTCHours(0, 0, 0, 0)
      }

      return ranges
    },

    mergeDailyStatsByStaffSummaries(summaries, weekStartIso, weekEndIso) {
      const weekStart = weekStartIso || ''
      const weekEnd = weekEndIso || ''
      const byStaff = { ...this.dailyStatsByStaff }
      const byStaffDate = {}

      for (const [staffId, stats] of Object.entries(byStaff)) {
        byStaff[staffId] = stats.filter(stat => stat.date < weekStart || stat.date > weekEnd)
      }

      for (const summary of summaries) {
        const sid = summary.staffId
        if (!sid) continue
        if (!byStaff[sid]) byStaff[sid] = []
        byStaff[sid].push(summaryToDailyStat(summary))
      }

      for (const [staffId, stats] of Object.entries(byStaff)) {
        stats.sort((a, b) => (a.date < b.date ? -1 : 1))
        byStaffDate[staffId] = {}
        for (const stat of stats) {
          byStaffDate[staffId][stat.date] = stat
        }
      }

      this.dailyStatsByStaff = byStaff
      this.dailyStatsByStaffDateIndex = byStaffDate
    },

    // 7) derive per-staff dailyStats
    async fetchDailyStatsByStaff({
      limit = 100,
      startDate,
      endDate,
      progressive = false
    } = {}) {
      const rangeKey = `${startDate || ''}-${endDate || ''}-${limit}`
      if (this.activitySummaryCancelRequested) {
        this.activitySummaryCancelRequested = false
      }
      if (pendingDailyStatsByStaffRequests.has(rangeKey)) {
        console.info('[Staff Insights] reusing in-flight dailyStatsByStaff fetch', {
          durationMs: 0,
          rangeKey,
          startDate,
          endDate,
          limit
        })
        return pendingDailyStatsByStaffRequests.get(rangeKey)
      }

      if (this.dailyStatsByStaffRangeKey === rangeKey && Object.keys(this.dailyStatsByStaff).length) {
        console.info('[Staff Insights] skipping dailyStatsByStaff fetch; cached range key still active', {
          durationMs: 0,
          rangeKey,
          staffCount: Object.keys(this.dailyStatsByStaff).length
        })
        return
      }

      const requestPromise = (async () => {
        if (progressive) {
          await this.fetchDailyStatsByStaffByWeek({ limit, startDate, endDate, rangeKey })
          return
        }

        const fetchStartedAt = performance.now()
        console.info('[Staff Insights] dailyStatsByStaff fetch started', {
          durationMs: 0,
          rangeKey,
          startDate,
          endDate,
          limit
        })
        const summaries = await this.fetchActivitySummaryData({ limit, startDate, endDate })
        const fetchCompletedAt = performance.now()
        console.info('[Staff Insights] mapping summaries to dailyStatsByStaff', {
          durationMs: Math.round(fetchCompletedAt - fetchStartedAt),
          rangeKey,
          summaryCount: summaries.length,
          fetchSummaryMs: Math.round(fetchCompletedAt - fetchStartedAt),
          firstSummary: summaries[0] || null
        })

        const mapStartedAt = performance.now()
        const byStaff = {}
        const byStaffDate = {}
        for (const summary of summaries) {
          const sid = summary.staffId
          if (!sid) continue
          if (!byStaff[sid]) byStaff[sid] = []
          if (!byStaffDate[sid]) byStaffDate[sid] = {}
          const dailyStat = summaryToDailyStat(summary)
          byStaff[sid].push(dailyStat)
          byStaffDate[sid][dailyStat.date] = dailyStat
        }
        const groupedAt = performance.now()

        for (const stats of Object.values(byStaff)) {
          stats.sort((a, b) => (a.date < b.date ? -1 : 1))
        }
        const sortedAt = performance.now()
        this.dailyStatsByStaff = byStaff
        this.dailyStatsByStaffDateIndex = byStaffDate
        this.dailyStatsByStaffRangeKey = rangeKey
        const assignedAt = performance.now()
        console.info('[Staff Insights] dailyStatsByStaff updated', {
          durationMs: Math.round(assignedAt - mapStartedAt),
          rangeKey,
          staffCount: Object.keys(byStaff).length,
          statCount: Object.values(byStaff).reduce((total, stats) => total + stats.length, 0),
          groupMs: Math.round(groupedAt - mapStartedAt),
          sortMs: Math.round(sortedAt - groupedAt),
          assignMs: Math.round(assignedAt - sortedAt),
          totalProcessMs: Math.round(assignedAt - mapStartedAt),
          sampleStaffId: Object.keys(byStaff)[0] || null,
          sampleStats: Object.values(byStaff)[0]?.slice(0, 3) || []
        })
      })()

      pendingDailyStatsByStaffRequests.set(rangeKey, requestPromise)
      try {
        return await requestPromise
      } finally {
        pendingDailyStatsByStaffRequests.delete(rangeKey)
      }
    },

    async fetchDailyStatsByStaffByWeek({ limit = 100, startDate, endDate, rangeKey } = {}) {
      const weeks = this.buildWeeklySummaryRanges(startDate, endDate)
      this.activitySummaryCancelRequested = false
      this.dailyStatsByStaff = {}
      this.dailyStatsByStaffDateIndex = {}
      this.dailyStatsByStaffRangeKey = rangeKey
      this.dailyStatsByStaffLoadingWeeks = {}
      this.dailyStatsByStaffLoadedWeeks = {}
      this.dailyStatsByStaffErrorWeeks = {}
      this.dailyStatsByStaffLoadProgress = {
        totalWeeks: weeks.length,
        completedWeeks: 0,
        activeWeeks: 0,
        failedWeeks: 0,
        currentWeekLabel: ''
      }
      this.isActivitySummaryLoading = true

      for (const week of weeks) {
        this.dailyStatsByStaffLoadingWeeks = {
          ...this.dailyStatsByStaffLoadingWeeks,
          [week.key]: true
        }
        this.dailyStatsByStaffErrorWeeks = {
          ...this.dailyStatsByStaffErrorWeeks,
          [week.key]: null
        }
      }

      const startedAt = performance.now()
      console.info('[Staff Insights] weekly summary fetch started', {
        durationMs: 0,
        rangeKey,
        weekCount: weeks.length,
        maxConcurrency: MAX_CONCURRENT_WEEK_SUMMARY_REQUESTS
      })

      let nextWeekIndex = 0
      const workerCount = Math.min(MAX_CONCURRENT_WEEK_SUMMARY_REQUESTS, weeks.length)

      const fetchWeek = async (week) => {
        if (this.activitySummaryCancelRequested) {
          return
        }
        const weekStartedAt = performance.now()
        this.dailyStatsByStaffLoadProgress = {
          ...this.dailyStatsByStaffLoadProgress,
          activeWeeks: this.dailyStatsByStaffLoadProgress.activeWeeks + 1,
          currentWeekLabel: `${week.startIso} - ${week.endIso}`
        }
        console.info('[Staff Insights] weekly summary chunk started', {
          durationMs: 0,
          rangeKey,
          weekKey: week.key,
          startDate: week.startDate,
          endDate: week.endDate
        })

        const summaries = await this.fetchActivitySummaryData({
          limit,
          startDate: week.startDate,
          endDate: week.endDate,
          useGlobalLoading: false
        })

        if (this.activitySummaryCancelRequested || this.dailyStatsByStaffRangeKey !== rangeKey) {
          return
        }

        if (this.error) {
          this.dailyStatsByStaffErrorWeeks = {
            ...this.dailyStatsByStaffErrorWeeks,
            [week.key]: this.error
          }
          this.dailyStatsByStaffLoadProgress = {
            ...this.dailyStatsByStaffLoadProgress,
            failedWeeks: this.dailyStatsByStaffLoadProgress.failedWeeks + 1
          }
        } else {
          this.mergeDailyStatsByStaffSummaries(summaries, week.startIso, week.endIso)
          this.dailyStatsByStaffLoadedWeeks = {
            ...this.dailyStatsByStaffLoadedWeeks,
            [week.key]: true
          }
        }

        this.dailyStatsByStaffLoadingWeeks = {
          ...this.dailyStatsByStaffLoadingWeeks,
          [week.key]: false
        }

        this.dailyStatsByStaffLoadProgress = {
          ...this.dailyStatsByStaffLoadProgress,
          completedWeeks: this.dailyStatsByStaffLoadProgress.completedWeeks + 1,
          activeWeeks: Math.max(0, this.dailyStatsByStaffLoadProgress.activeWeeks - 1)
        }

        console.info('[Staff Insights] weekly summary chunk completed', {
          durationMs: Math.round(performance.now() - weekStartedAt),
          rangeKey,
          weekKey: week.key,
          summaryCount: summaries.length,
          loadedWeekCount: Object.keys(this.dailyStatsByStaffLoadedWeeks).length,
          loadingWeekCount: Object.values(this.dailyStatsByStaffLoadingWeeks).filter(Boolean).length
        })
      }

      const runWorker = async () => {
        while (nextWeekIndex < weeks.length) {
          if (this.activitySummaryCancelRequested) {
            return
          }
          const week = weeks[nextWeekIndex]
          nextWeekIndex += 1
          await fetchWeek(week)
        }
      }

      try {
        await Promise.all(Array.from({ length: workerCount }, runWorker))
      } finally {
        this.isActivitySummaryLoading = false
        if (this.activitySummaryCancelRequested) {
          this.dailyStatsByStaffLoadingWeeks = {}
          this.dailyStatsByStaffLoadProgress = {
            ...this.dailyStatsByStaffLoadProgress,
            activeWeeks: 0
          }
        }
      }

      console.info('[Staff Insights] weekly summary fetch completed', {
        durationMs: Math.round(performance.now() - startedAt),
        rangeKey,
        weekCount: weeks.length,
        loadedWeekCount: Object.keys(this.dailyStatsByStaffLoadedWeeks).length,
        staffCount: Object.keys(this.dailyStatsByStaff).length,
        statCount: Object.values(this.dailyStatsByStaff).reduce((total, stats) => total + stats.length, 0)
      })
    },

    cancelEmployeeInsightsLoading() {
      this.activitySummaryCancelRequested = true
      for (const controller of activitySummaryAbortControllers.values()) {
        controller.abort()
      }
      activitySummaryAbortControllers.clear()
      pendingActivitySummaryRequests.clear()
      pendingDailyStatsByStaffRequests.clear()
      this.isLoading = false
      this.isActivitySummaryLoading = false
      this.dailyStatsByStaffLoadingWeeks = {}
      this.dailyStatsByStaffLoadProgress = {
        ...this.dailyStatsByStaffLoadProgress,
        activeWeeks: 0
      }
      console.info('[Staff Insights] employee insights loading canceled', {
        durationMs: 0
      })
    },

    clearSelectedStaff() {
      this.selectedStaff = null
      this.error = null
    },

    clearDailyStats() {
      this.dailyStats = []
      this.dailyStatsRangeKey = null
    },

    clearDailyStatsByStaff() {
      this.dailyStatsByStaff = {}
      this.dailyStatsByStaffDateIndex = {}
      this.dailyStatsByStaffRangeKey = null
      this.dailyStatsByStaffLoadingWeeks = {}
      this.dailyStatsByStaffLoadedWeeks = {}
      this.dailyStatsByStaffErrorWeeks = {}
      this.dailyStatsByStaffLoadProgress = {
        totalWeeks: 0,
        completedWeeks: 0,
        activeWeeks: 0,
        failedWeeks: 0,
        currentWeekLabel: ''
      }
    },

    updateDateRange(startEpoch, endEpoch) {
      this.startDate = startEpoch
      this.endDate = endEpoch
      this.persistDateRange()
    },

    async loadWeeklyHourTargets() {
      try {
        const response = await getEmployeeInsightsWeeklyTargets()
        const defaultTarget = Number(response.data?.defaultWeeklyHourTarget)
        this.defaultWeeklyHourTarget = Number.isFinite(defaultTarget) && defaultTarget > 0
          ? defaultTarget
          : 40
        this.weeklyHourTargets = response.data?.weeklyHourTargets || {}
      } catch (err) {
        console.warn('Failed to load weekly hour targets from middleware:', err)
        this.defaultWeeklyHourTarget = 40
        this.weeklyHourTargets = {}
      }
    },

    persistDateRange() {
      if (typeof window === 'undefined') return
      try {
        window.localStorage.setItem(
          'employeeInsightsDateRange',
          JSON.stringify({ startDate: this.startDate, endDate: this.endDate })
        )
      } catch (err) {
        console.warn('Failed to persist employee insights date range:', err)
      }
    },
  },

  getters: {
    activeStaff: (state) =>
      state.staffList.filter(s => s.standing !== 'inactive'),

    weeklyTargetForStaff: (state) => (staffId) =>
      state.weeklyHourTargets?.[staffId] ?? state.defaultWeeklyHourTarget,
  }
})
